import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import CheckYours from "./CheckYours";
import ContactUs from "./ContactUs";
import IngredientSheet, { type Data } from "./IngredientSheet";
import KeepClose, { KEEP_ID } from "./KeepClose";
import KeepFree, { WhyAdsLink } from "./KeepFree";
import PartnerSpot from "./PartnerSpot";
import ProductGrid, { CategoryBar, type Finder, type Product, toGroups, useProducts, verifiedWithin } from "./ProductGrid";
import { fill, LangProvider, LanguageSwitcher, useLang } from "./i18n";
import { useMessages } from "./messages";
import { signal, stripUtm } from "./pulse";
import { registerPwa, useInstall } from "./pwa";
import { ChannelLink } from "./social";
import Standard, { CheckerPulse, openStandard, type StandardData, useStandard } from "./Standard";

const SITE = "https://bloom.obto.co";
// One page per product (BloomGrowthService.pageHtml): its own title, description and share preview.
const PAGE_PATH = "/api/bloom/p/";
// GT-1: the page the consent loader last counted. Opening or closing a sheet changes the address,
// and each new address is one page view (a no-op until the visitor allows measurement).
let pageViewKey = "";
// B15: the server-rendered blog (BloomBlogService).
const BLOG_PATH = "/api/bloom/blog";
// B30: the "Answers" hub (BloomSeoService.solveIndexHtml), linked from the header and the footer.
const SOLVE_PATH = "/api/bloom/solve";
const ANSWERS: Record<string, string> = { en: "Answers", hi: "जवाब", pa: "ਜਵਾਬ", fr: "Réponses", es: "Respuestas" };
const ASIN_RE = /^[A-Z0-9]{10}$/;
const CHECK_RE = /^[a-f0-9]{16}$/;

type Sheet = { kind: "p"; asin: string } | { kind: "c"; id: string; initial: Data | null } | null;

// Deep links: /api/bloom/p/<ASIN> or ?p=<ASIN> opens a product sheet, ?c=<id> opens a "Check yours" result.
// B30: /api/bloom/p/<ASIN> is now a server-rendered page, and index.html sends ?p= there; its "Open Bloom"
// button comes back with ?open=<ASIN>, which opens the sheet in the app.
function sheetFromUrl(): Sheet {
    try {
        const q = new URLSearchParams(window.location.search);
        const path = window.location.pathname.indexOf(PAGE_PATH) === 0 ? window.location.pathname.slice(PAGE_PATH.length) : "";
        const p = (path || q.get("open") || q.get("p") || "").toUpperCase();
        if (ASIN_RE.test(p)) return { kind: "p", asin: p };
        const c = (q.get("c") || "").toLowerCase();
        if (CHECK_RE.test(c)) return { kind: "c", id: c, initial: null };
    } catch (e) {}
    return null;
}

// B30: ?cat=<shelf> opens the grid on one shelf (product pages link their shelf this way).
const CAT_RE = /^(?:new|food|drink|supplement|personal_care|baby|household|textile|furniture|other)$/;
function catFromUrl(): string {
    try {
        const c = (new URLSearchParams(window.location.search).get("cat") || "").toLowerCase();
        if (CAT_RE.test(c)) return c;
    } catch (e) {}
    return "all";
}

// A family's other sizes and colours share the family's page (same rule as the server).
function pageAsin(asin: string, products: Product[]): string {
    const fam = products.find((p) => p.asin === asin)?.family?.id;
    return fam && products.some((p) => p.asin === fam && p.family?.id === fam) ? fam : asin;
}

function productLabel(p: Product): string {
    const name = p.family?.name || p.title;
    const brand = p.family?.brand || p.brand;
    return brand && !/^generic$/i.test(brand) && !name.toLowerCase().includes(brand.toLowerCase()) ? name + " (" + brand + ")" : name;
}

// The verified list as an ItemList (one entry per card, linking to its page), for search engines.
function setItemList(products: Product[], lang: string) {
    const seen = new Set<string>();
    const items: { "@type": string; position: number; url: string; name: string }[] = [];
    for (const p of products) {
        const a = pageAsin(p.asin, products);
        if (seen.has(a)) continue;
        seen.add(a);
        items.push({ "@type": "ListItem", position: items.length + 1, url: SITE + PAGE_PATH + a + (lang === "en" ? "" : "?lang=" + lang), name: productLabel(p) });
    }
    let el = document.getElementById("bloom-itemlist");
    if (!el) {
        el = document.createElement("script");
        el.id = "bloom-itemlist";
        el.setAttribute("type", "application/ld+json");
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify({ "@context": "https://schema.org", "@type": "ItemList", itemListElement: items });
}

function setCanonical(href: string) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
    }
    link.href = href;
}

function FlaskIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
            <path d="M9 3h6" />
            <path d="M10 3v6.5L4.6 18.2A1.8 1.8 0 0 0 6.1 21h11.8a1.8 1.8 0 0 0 1.5-2.8L14 9.5V3" />
            <path d="M7.5 15h9" />
        </svg>
    );
}

function SearchIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className={className} aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
        </svg>
    );
}

// Always-visible finder in the sticky header. "/" focuses it from anywhere.
function SearchBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
    const { t } = useLang();
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement | null)?.tagName;
            if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
                e.preventDefault();
                ref.current?.focus();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    return (
        <div className="relative mt-3">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-green-700/60" />
            <input
                ref={ref}
                type="search"
                value={value}
                onChange={(e) => onChange(e.target.value.slice(0, 80))}
                placeholder={t.searchPlaceholder}
                aria-label={t.searchPlaceholder}
                className="w-full rounded-full border border-green-100 bg-white py-2.5 pl-10 pr-20 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-100 [&::-webkit-search-cancel-button]:hidden"
            />
            {value ? (
                <button
                    type="button"
                    onClick={() => { onChange(""); ref.current?.focus(); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-200"
                >
                    {t.clear}
                </button>
            ) : (
                <kbd className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-gray-200 px-1.5 text-[10px] text-gray-400 sm:block">/</kbd>
            )}
        </div>
    );
}

function ShieldIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
            <path d="M12 3 4.5 6v5.5c0 4.6 3.2 8.1 7.5 9.5 4.3-1.4 7.5-4.9 7.5-9.5V6L12 3Z" />
            <path d="m8.5 12 2.5 2.5 4.5-5" />
        </svg>
    );
}

// How many product types are covered (one card each, contract rule 8) and how many joined lately:
// covered types only grow. Sizes and colours are the secondary count.
function GrowthCounter({ fallback, products }: { fallback: number; products: Product[] }) {
    const m = useMessages();
    const groups = toGroups(products.map((p) => ({ p, hit: null })));
    const total = groups.length || fallback;
    if (!total) return null;
    const within = (h: number) => groups.filter((g) => g.members.some((p) => verifiedWithin(p, h))).length;
    const day = within(24);
    const week = within(24 * 7);
    return (
        <button
            type="button"
            onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="mt-6 flex items-center gap-3 rounded-2xl bg-white/90 px-4 py-3 text-left shadow-md ring-1 ring-green-100 transition hover:ring-green-300"
        >
            <span className="text-4xl font-extrabold leading-none tabular-nums text-green-700 sm:text-5xl">{total.toLocaleString()}</span>
            <span className="leading-tight">
                <span className="block text-sm font-bold text-gray-900 sm:text-base">{m.verifiedProducts}</span>
                {products.length > groups.length ? (
                    <span className="block text-xs text-gray-500">{fill(m.variantsCount, { n: products.length })}</span>
                ) : null}
                {day || week ? (
                    <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-extrabold text-emerald-800">
                        <span aria-hidden="true">▲</span>{day ? fill(m.growth24, { n: day }) : fill(m.growth7, { n: week })}
                    </span>
                ) : null}
            </span>
        </button>
    );
}

// B23: three picks, each with its Amazon button, right under the headline (a column on phones, a row on wider
// screens). Social visitors land on a phone and rarely scroll; from 10-19 the check hook alone filled that screen
// and Amazon taps fell to about 0. On a 1280x800 desktop the first button sat at 1333 px. The check hook follows.
function PhonePicks({ products, onOpen }: { products: Product[] | null; onOpen: (asin: string) => void }) {
    const { t } = useLang();
    const seen = new Set<string>();
    const picks: Product[] = [];
    for (const p of products || []) {
        const k = p.family?.id || p.asin;
        if (!p.goUrl || seen.has(k)) continue;
        seen.add(k);
        picks.push(p);
        if (picks.length === 3) break;
    }
    if (!picks.length) return null;
    return (
        <ul className="mt-4 grid gap-2 sm:grid-cols-3">
            {picks.map((p) => (
                <li key={p.asin} className="flex items-center gap-3 rounded-2xl bg-white p-2 shadow-sm ring-1 ring-green-100">
                    {p.imageUrl ? (
                        <button type="button" onClick={() => onOpen(p.asin)} className="shrink-0">
                            <img src={p.imageUrl} alt={p.title} className="h-14 w-14 object-contain" />
                        </button>
                    ) : null}
                    <div className="min-w-0 flex-1">
                        <button type="button" onClick={() => onOpen(p.asin)} className="block w-full text-left">
                            {p.brand ? <span className="block truncate text-[10px] font-bold uppercase tracking-wider text-green-700">{p.brand}</span> : null}
                            <span className="line-clamp-1 text-sm font-bold leading-snug text-gray-900">{p.family?.name || p.title}</span>
                        </button>
                        <a
                            href={p.goUrl}
                            onClick={() => signal("amazon_tap", { a: p.asin })}
                            target="_blank"
                            rel="sponsored nofollow noopener noreferrer"
                            data-obto-cta="amazon"
                            className="mt-1 block rounded-lg bg-green-600 px-2 py-1.5 text-center text-xs font-semibold text-white transition hover:bg-green-700"
                        >
                            {t.checkPrice}
                        </a>
                    </div>
                </li>
            ))}
        </ul>
    );
}

// The first thing a parent reads: what we check (what they eat, sleep in, put on their skin),
// the promise (nothing else gets in, nobody pays), the verified count, and two ways forward. Right under the
// headline, inside the first phone screen, the personal hook: check a product from your own bathroom (a result
// a visitor can share).
function Hero({ std, analysed, products, onCheck, onOpen }: { std: StandardData | null; analysed: number; products: Product[] | null; onCheck: () => void; onOpen: (asin: string) => void }) {
    const { t } = useLang();
    const m = useMessages();
    const { mode, install } = useInstall();
    const seeList = () => document.getElementById("products")?.scrollIntoView({ behavior: "smooth", block: "start" });
    const getApp = async () => {
        signal("app_tap", { s: "hero_" + mode });
        if (mode === "prompt" && (await install())) return;
        document.getElementById(KEEP_ID)?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    return (
        <section className="relative overflow-hidden border-b border-green-100 bg-gradient-to-br from-green-50 via-white to-emerald-50 px-4 py-8 sm:px-6 sm:py-12">
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-emerald-200/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-20 left-10 h-48 w-48 rounded-full bg-lime-200/40 blur-3xl" />
            <div className="relative mx-auto max-w-3xl">
                <p className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 text-xs font-bold text-green-800 shadow-sm ring-1 ring-green-100">
                    <span aria-hidden="true">🌱</span> {m.heroEyebrow}
                </p>
                <h1 className="mt-3 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 sm:text-5xl">{t.tagline}</h1>
                <PhonePicks products={products} onOpen={onOpen} />
                <button
                    type="button"
                    onClick={onCheck}
                    className="mt-4 flex w-full items-center gap-3 rounded-2xl bg-white px-4 py-3 text-left shadow-lg ring-2 ring-green-500/70 transition hover:-translate-y-0.5 active:scale-[.98] sm:max-w-md"
                >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-green-600 text-xl text-white" aria-hidden="true">📸</span>
                    <span className="min-w-0 leading-tight">
                        <span className="block text-base font-extrabold text-gray-900">{m.heroCheckQ}</span>
                        <span className="mt-0.5 block text-xs text-gray-600">{m.heroCheckSub}</span>
                    </span>
                    <span className="ml-auto text-2xl font-bold text-green-700" aria-hidden="true">›</span>
                </button>
                <p className="mt-4 text-base leading-relaxed text-gray-700 sm:text-lg">{t.heroSub}</p>
                <p className="mt-4 border-l-4 border-green-500 pl-3 text-sm font-semibold italic leading-relaxed text-green-800 sm:text-base">{m.heroOneBody}</p>
                <GrowthCounter fallback={std?.listed || 0} products={products || []} />
                <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={seeList}
                        className="inline-flex items-center gap-2 rounded-2xl bg-green-600 px-5 py-3 text-base font-bold text-white shadow-xl shadow-green-600/25 transition hover:-translate-y-0.5 hover:bg-green-700 active:scale-95"
                    >
                        {m.heroSee} <span aria-hidden="true">↓</span>
                    </button>
                    {mode !== "installed" ? (
                        <button
                            type="button"
                            onClick={getApp}
                            className="inline-flex items-center gap-2 rounded-2xl bg-white px-5 py-3 text-base font-bold text-green-800 shadow-md ring-1 ring-green-200 transition hover:-translate-y-0.5 hover:ring-green-400 active:scale-95"
                        >
                            <span aria-hidden="true">📲</span> {m.heroApp}
                        </button>
                    ) : null}
                </div>
                <div className="mt-5 flex flex-wrap gap-2 text-xs font-semibold text-gray-700">
                    <CheckerPulse data={std} />
                    {std && std.checked ? (
                        <button
                            type="button"
                            onClick={openStandard}
                            className="inline-flex items-center gap-1.5 rounded-full bg-white/80 px-3 py-1 shadow-sm ring-1 ring-green-100 transition hover:ring-green-300"
                        >
                            <ShieldIcon className="h-3.5 w-3.5 text-green-700" />
                            {fill(t.stdChecked, { checked: std.checked.toLocaleString(), passed: std.passed.toLocaleString() })}
                            <span className="text-green-700" aria-hidden="true">›</span>
                        </button>
                    ) : analysed ? (
                        <span className="rounded-full bg-white/80 px-3 py-1 shadow-sm ring-1 ring-green-100">🧪 {fill(t.statProducts, { n: analysed })}</span>
                    ) : null}
                    <span className="rounded-full bg-white/80 px-3 py-1 shadow-sm ring-1 ring-green-100">🌐 {t.statLangs}</span>
                    <span className="rounded-full bg-white/80 px-3 py-1 shadow-sm ring-1 ring-green-100">✓ {t.statFree}</span>
                </div>
            </div>
        </section>
    );
}

function App() {
    const { t, lang } = useLang();
    const m = useMessages();
    const [isOpen, setIsOpen] = useState(false);
    const [sheet, setSheet] = useState<Sheet>(sheetFromUrl);
    const [checking, setChecking] = useState(false);
    // The hero hook promises "snap the label", so it opens the check on the Photo tab.
    const [checkStart, setCheckStart] = useState<"text" | "photo">("text");
    const [finder, setFinder] = useState<Finder>(() => ({ query: "", mode: "contains", sort: "all", cat: catFromUrl() }));
    const products = useProducts(lang);
    const std = useStandard(lang);
    const analysed = products.status === "ready" ? products.products.filter((p) => p.ingredients).length : 0;
    const prevQuery = useRef("");

    // First keystroke brings the results into view (the hero can fill a phone screen).
    useEffect(() => {
        if (finder.query && !prevQuery.current) {
            document.getElementById("products")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        prevQuery.current = finder.query;
    }, [finder.query]);

    useEffect(() => {
        // Child effects run before LangProvider stamps <html lang>, so pass the language explicitly.
        signal("view", { l: lang });
        stripUtm();
    }, []);

    // Keep the address bar, title and canonical in step with the open sheet, so a copied URL opens
    // (and previews) the same thing: an open product lives at its own page, /api/bloom/p/<ASIN>.
    const list = products.status === "ready" ? products.products : null;
    useEffect(() => {
        try {
            const u = new URL(window.location.href);
            u.searchParams.delete("p");
            u.searchParams.delete("c");
            u.searchParams.delete("open");
            if (sheet?.kind === "p") u.pathname = PAGE_PATH + sheet.asin;
            else if (u.pathname.indexOf(PAGE_PATH) === 0) u.pathname = "/";
            if (sheet?.kind === "c") u.searchParams.set("c", sheet.id);
            window.history.replaceState(null, "", u.toString());
            const k = u.pathname + "?" + (u.searchParams.get("c") || "");
            if (pageViewKey && k !== pageViewKey) (window as any).obtoMarketingPageView?.();
            pageViewKey = k;
        } catch (e) {}
        const langQ = lang === "en" ? "" : "?lang=" + lang;
        if (sheet?.kind === "p") {
            setCanonical(SITE + PAGE_PATH + pageAsin(sheet.asin, list || []) + langQ);
            const p = list?.find((x) => x.asin === sheet.asin);
            if (p) document.title = fill(t.pageTitle, { product: productLabel(p) });
        } else {
            setCanonical(SITE + "/" + langQ);
            document.title = t.docTitle;
        }
    }, [sheet, lang, list, t.docTitle, t.pageTitle]);

    useEffect(() => {
        if (list) setItemList(list, lang);
    }, [list, lang]);

    useEffect(() => {
        if (sheet) signal("sheet_open", sheet.kind === "p" ? { a: sheet.asin } : { c: sheet.id });
    }, [sheet]);

    const openProduct = useCallback((asin: string) => setSheet({ kind: "p", asin }), []);
    const closeSheet = useCallback(() => setSheet(null), []);
    const openCheck = useCallback(() => { setSheet(null); setCheckStart("text"); setChecking(true); }, []);
    const openPhotoCheck = useCallback(() => { setSheet(null); setCheckStart("photo"); setChecking(true); }, []);
    const closeCheck = useCallback(() => setChecking(false), []);
    const onResult = useCallback((d: Data) => {
        setChecking(false);
        if (d.checkId) setSheet({ kind: "c", id: d.checkId, initial: d });
    }, []);

    return (
        <div className="min-h-screen flex flex-col bg-white">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b border-green-100 bg-green-50/90 px-4 py-3 backdrop-blur sm:px-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <a href={lang === "en" ? "/" : "/?lang=" + lang} className="text-2xl font-bold text-green-700">Bloom</a>
                    <nav className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
                        <button
                            onClick={openStandard}
                            className="hidden items-center gap-1.5 rounded-full bg-green-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-green-700 sm:inline-flex"
                        >
                            <ShieldIcon className="h-3.5 w-3.5" />
                            {t.stdTitle}
                        </button>
                        <a href={BLOG_PATH} className="hidden font-semibold text-green-700 hover:underline sm:inline">{m.blog}</a>
                        <a href={SOLVE_PATH} className="hidden font-semibold text-green-700 hover:underline sm:inline">{ANSWERS[lang] || ANSWERS.en}</a>
                        <button onClick={() => setIsOpen(true)} className="hidden hover:text-green-700 sm:inline">
                            {t.ourStory}
                        </button>
                        <span className="hidden sm:inline-flex"><ContactUs /></span>
                        <LanguageSwitcher />
                    </nav>
                </div>
                <SearchBox value={finder.query} onChange={(query) => setFinder((f) => ({ ...f, query }))} />
                {products.status === "ready" ? (
                    <CategoryBar
                        products={products.products}
                        value={finder.cat}
                        onChange={(cat) => {
                            setFinder((f) => ({ ...f, cat }));
                            document.getElementById("products")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                    />
                ) : null}
            </header>

            <Hero std={std} analysed={analysed} products={products.status === "ready" ? products.products : null} onCheck={openPhotoCheck} onOpen={openProduct} />

            <div className="flex flex-1">
                {/* About rail (wide screens only) */}
                <aside className="hidden xl:block w-64 shrink-0 p-6">
                    <div className="sticky top-44 rounded-2xl bg-green-50 p-5">
                        <h2 className="mb-3 text-lg font-semibold text-green-700">{t.whyTitle}</h2>
                        <p className="mb-4 text-sm text-gray-600">{t.whyShort}</p>
                        <button
                            onClick={() => setIsOpen(true)}
                            className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700"
                        >
                            {t.readMore}
                        </button>
                    </div>
                </aside>

                {/* Product grid: live catalog from /api/bloom/products */}
                <ProductGrid state={products} finder={finder} onFinder={setFinder} onIngredients={openProduct} onCheck={openCheck} />

                {/* Sticky sponsored rail (desktop): always visible, never over content */}
                <PartnerSpot variant="rail" />
            </div>

            <KeepClose />

            {/* Footer */}
            <footer className="mt-6 bg-green-50 p-4 text-center text-sm text-gray-700">
                <div className="mb-3 flex items-center justify-center gap-3 sm:hidden">
                    <button onClick={() => setIsOpen(true)} className="hover:text-green-700">{t.ourStory}</button>
                    <ContactUs />
                </div>
                <p className="mx-auto mb-2 max-w-2xl">{t.footerFund} <WhyAdsLink /></p>
                <ChannelLink />
                <p className="mb-2">
                    <button onClick={openStandard} className="font-semibold text-green-700 hover:underline">{t.stdTitle}</button>
                    <span className="mx-2 text-gray-300">·</span>
                    <button onClick={openCheck} className="inline-flex items-center gap-1 font-semibold text-green-700 hover:underline">
                        <FlaskIcon className="h-3.5 w-3.5" />
                        {t.checkLabel}
                    </button>
                    <span className="mx-2 text-gray-300">·</span>
                    <a href={BLOG_PATH} className="font-semibold text-green-700 hover:underline">{m.blog}</a>
                    <span className="mx-2 text-gray-300">·</span>
                    <a href={SOLVE_PATH} className="font-semibold text-green-700 hover:underline">{ANSWERS[lang] || ANSWERS.en}</a>
                    <span className="mx-2 text-gray-300">·</span>
                    <a href="/api/bloom/about" className="hover:text-green-700 hover:underline">About</a>
                    <span className="mx-2 text-gray-300">·</span>
                    <a href="/api/bloom/contact" className="hover:text-green-700 hover:underline">Contact</a>
                    <span className="mx-2 text-gray-300">·</span>
                    <a href="https://www.obto.co/privacy" rel="noopener" className="hover:text-green-700 hover:underline">Privacy</a>
                </p>
                © Bloom 2025-2026 • {t.disclosure}
            </footer>

            <KeepFree />
            <Standard data={std} />

            {sheet?.kind === "p" ? <IngredientSheet asin={sheet.asin} onClose={closeSheet} /> : null}
            {sheet?.kind === "c" ? (
                <IngredientSheet checkId={sheet.id} initial={sheet.initial} onClose={closeSheet} onCheckAnother={sheet.initial ? openCheck : openPhotoCheck} arrived={!sheet.initial} />
            ) : null}
            {checking ? <CheckYours start={checkStart} onClose={closeCheck} onResult={onResult} /> : null}

            {/* Our Story */}
            {isOpen && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200" onClick={() => setIsOpen(false)}>
                    <div
                        className="relative max-w-lg rounded-2xl bg-white p-6 shadow-xl animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="absolute right-3 top-3 text-2xl text-gray-600 hover:text-black"
                            aria-label={t.close}
                            onClick={() => setIsOpen(false)}
                        >
                            &times;
                        </button>
                        <h2 className="mb-4 text-xl font-bold">{t.whyTitle}</h2>
                        <p className="mb-4 text-gray-700">{t.whyLong}</p>
                        <button
                            onClick={() => setIsOpen(false)}
                            className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                        >
                            {t.close}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Before mount: the browser's install offer can arrive before React is ready.
registerPwa();

// Canonical OBTO mount — one module entry, one React instance.
const mount = () => {
    const el = document.getElementById("root");
    if (!el) return;
    createRoot(el).render(
        <LangProvider>
            <App />
        </LangProvider>
    );
};
document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", mount, { once: true })
    : mount();
